'use client';

import { type FC, useState } from 'react';
import { cn } from '../../../utils/cn';
import { Avatar } from '../../ui';
import { FaCopy, FaShare, FaCrown, FaCheckCircle, FaTimesCircle, FaChevronDown } from '../../icons';
import { rushMidAvatarUrl } from './avatarUrl';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations from './RushMid.translations';
import { useRushMidParty } from './RushMidPartyContext';
import { useProfile } from '../../../queries/useProfileQueries';
import { JoinByCodeSheet } from './lobby/JoinByCodeSheet';

interface PartySquadStripProps {
  onCreateParty: () => void;
  onJoinParty: (code: string) => void;
  defaultExpanded?: boolean;
}

/** The «my strip» — party mechanics only, always visible on the games home screen. Solo mode
 *  is a party of one with no invite code (the backend mints one only once a party exists);
 *  in a real party it exposes the code, roster, ready state and leader controls. */
export const PartySquadStrip: FC<PartySquadStripProps> = ({ onCreateParty, onJoinParty, defaultExpanded }) => {
  const { t } = useLocalTranslation(translations);
  const party = useRushMidParty();
  const { data: profile } = useProfile();
  const [expanded, setExpanded] = useState(defaultExpanded ?? party != null);
  const [copied, setCopied] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  const copy = () => {
    if (!party) return;
    if (!('clipboard' in navigator)) {
      party.onShare();
      return;
    }
    navigator.clipboard.writeText(party.code).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }).catch(() => party.onShare());
  };

  return (
    <div className="rounded-2xl bg-bg-secondary p-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="truncate text-sm font-semibold text-label-primary">
          {party ? t('squadTitle') : t('inviteFriends')}
        </span>
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full text-label-secondary">
          <FaChevronDown size={16} className={cn('transition-transform', expanded && 'rotate-180')} />
        </span>
      </button>

      <div className="mt-2 flex items-center gap-1.5">
        {party
          ? party.members.map((m) => (
            <div key={m.userId} className="relative">
              <Avatar size="s" src={rushMidAvatarUrl(m.userId)} fallbackKey={m.userId} firstName={m.name} />
              <span className={cn('absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-bg-secondary', m.ready ? 'bg-green-500' : 'bg-white/25')} />
            </div>
          ))
          : (
            <Avatar size="s" src={rushMidAvatarUrl(profile?.id)} fallbackKey={profile?.id} firstName={profile?.firstName} />
          )}
        {(!party || party.isLeader) && (
          <button
            type="button"
            aria-label={t('gameInvite')}
            onClick={() => { setExpanded(true); if (!party) onCreateParty(); }}
            className="flex size-8 items-center justify-center rounded-full border-2 border-solid border-white/25 text-lg leading-none text-white/50 active:opacity-60"
          >
            +
          </button>
        )}
      </div>

      {expanded && (
        <div className="mt-3 flex flex-col gap-3 border-t border-white/10 pt-3">
          {party ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-label-secondary">{t('partyCodeLabel')}</div>
                  <div className="font-mono text-2xl font-black tracking-[0.22em] text-gold">{party.code}</div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-label-primary active:opacity-60">
                    <FaCopy size={12} /> {copied ? t('partyCopied') : t('partyCopy')}
                  </button>
                  <button type="button" onClick={party.onShare} disabled={party.sharing} aria-label={t('partyShareBtn')} className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-label-primary active:opacity-60 disabled:opacity-40">
                    <FaShare size={12} /> {t('partyShareBtn')}
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                {party.members.map((m) => {
                  const isLeaderRow = m.userId === party.leaderUserId;
                  const isYou = m.userId === party.youUserId;
                  return (
                    <div key={m.userId} className="flex items-center gap-2.5">
                      <Avatar size="s" src={rushMidAvatarUrl(m.userId)} fallbackKey={m.userId} firstName={m.name} className={cn(isLeaderRow && 'ring-2 ring-gold')} />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-label-primary">{isYou ? t('partyYou') : m.name}</span>
                      {isLeaderRow && <FaCrown size={13} className="shrink-0 text-gold" />}
                      <span className={cn('inline-flex items-center gap-1 text-xs font-semibold', m.ready ? 'text-green-500' : 'text-white/40')}>
                        {m.ready ? t('partyReadyYes') : t('partyReadyNo')}
                        {m.ready ? <FaCheckCircle size={13} /> : <FaTimesCircle size={13} />}
                      </span>
                      {party.isLeader && !isLeaderRow && (
                        <button type="button" aria-label={t('partyKick')} onClick={() => party.onKick(m.userId)} className="flex size-5 shrink-0 items-center justify-center rounded-full text-label-secondary active:opacity-60">
                          <FaTimesCircle size={15} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={party.onLeave}
                className="rounded-xl border border-white/10 py-2.5 text-sm font-semibold text-label-secondary active:opacity-60"
              >
                {t('partyLeave')}
              </button>
            </>
          ) : (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={onCreateParty}
                className="rounded-xl bg-white/10 py-2.5 text-sm font-semibold text-label-primary active:opacity-60"
              >
                {t('inviteFriends')}
              </button>
              <button
                type="button"
                onClick={() => setJoinOpen(true)}
                className="rounded-xl border border-white/10 py-2.5 text-sm font-semibold text-label-secondary active:opacity-60"
              >
                {t('partyJoin')}
              </button>
            </div>
          )}
        </div>
      )}

      <JoinByCodeSheet
        isOpen={joinOpen}
        onClose={() => setJoinOpen(false)}
        onJoin={(code) => onJoinParty(code)}
      />
    </div>
  );
};
